<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import { useAdmin } from 'dashboard/composables/useAdmin';
import TaskDatePicker from './TaskDatePicker.vue';
import ContactAPI from 'dashboard/api/contacts';
import { buildDueIso } from '../helpers/dueDate';

const props = defineProps({
  selectedIds: { type: Array, required: true },
});

const emit = defineEmits(['done', 'cancel', 'close']);

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const agents = useMapGetter('agents/getAgents');
const accountLabels = useMapGetter('labels/getLabels');

const count = computed(() => props.selectedIds.length);

// ── Estado acumulado de alterações pendentes ──────────────────────
const pendingChanges = ref({});
// { status, completed_at, due_date, assignee_id, priority, task_type,
//   label_add: [], label_remove: [], delete: false }

const hasPendingChanges = computed(() => {
  const p = pendingChanges.value;
  return Object.keys(p).some(k => {
    if (k === '_contactName') return false;
    if (k === 'label_add' || k === 'label_remove')
      return (p[k]?.length ?? 0) > 0;
    if (k === 'label_remove_all') return p[k] === true;
    return p[k] !== undefined && p[k] !== null;
  });
});

// ── Submenu ativo ─────────────────────────────────────────────────
const activeSubmenu = ref(null);
const labelSearch = ref('');
const labelSearchRef = ref(null);
const dateValue = ref({ due_date: null, due_time: null });

// ── Posição dos submenus via Teleport ────────────────────────────
const tagsBtnRef = ref(null);
const tagsRemoveBtnRef = ref(null);
const dateBtnRef = ref(null);
const assigneeBtnRef = ref(null);
const priorityBtnRef = ref(null);
const typeBtnRef = ref(null);
const contactBtnRef = ref(null);

const tagsAddSubmenuPos = ref({ top: 0, left: 0 });
const tagsRemoveSubmenuPos = ref({ top: 0, left: 0 });
const dateSubmenuPos = ref({ top: 0, left: 0 });
const assigneeSubmenuPos = ref({ top: 0, left: 0 });
const prioritySubmenuPos = ref({ top: 0, left: 0 });
const typeSubmenuPos = ref({ top: 0, left: 0 });
const contactSubmenuPos = ref({ top: 0, left: 0 });

const labelRemoveSearchRef = ref(null);

function calcSubmenuPos(btnRef, posRef, submenuWidth = 208) {
  if (!btnRef?.value) return;
  const rect = btnRef.value.getBoundingClientRect();
  const spaceRight = window.innerWidth - rect.right;
  if (spaceRight >= submenuWidth + 8) {
    posRef.value = { top: rect.top, left: rect.right + 4 };
  } else {
    posRef.value = { top: rect.top, left: rect.left - submenuWidth - 4 };
  }
}

// ── Busca e highlight dos submenus com lista ──────────────────────
const { avatarColor, initials } = useAvatarColor();

const assigneeSearch = ref('');
const prioritySearch = ref('');
const typeSearch = ref('');
const assigneeHighlight = ref(0);
const priorityHighlight = ref(0);
const typeHighlight = ref(0);

const contactSearch = ref('');
const contactResults = ref([]);
const contactHighlight = ref(0);
const contactSearching = ref(false);

function normalizeContact(c) {
  return {
    ...c,
    company_name:
      c.company_name ?? c.additional_attributes?.company_name ?? null,
  };
}

async function searchContacts(query) {
  if (!query || query.length < 1) {
    contactResults.value = [];
    return;
  }
  contactSearching.value = true;
  try {
    const { data } = await ContactAPI.search(query, 1, 'name');
    contactResults.value = (data?.payload || []).map(normalizeContact);
    contactHighlight.value = 0;
  } catch {
    contactResults.value = [];
  } finally {
    contactSearching.value = false;
  }
}

const PRIORITY_OPTIONS = [
  { key: 'urgent', label: t('TASKS.PRIORITY.URGENT'), dot: 'text-n-ruby-9' },
  { key: 'high', label: t('TASKS.PRIORITY.HIGH'), dot: 'text-n-amber-9' },
  { key: 'normal', label: t('TASKS.PRIORITY.NORMAL'), dot: 'text-n-blue-9' },
  {
    key: 'no_priority',
    label: t('TASKS.PRIORITY.NONE'),
    dot: 'text-n-slate-8',
  },
];

const TASK_TYPES = [
  {
    key: 'general',
    label: t('TASKS.TYPES.GENERAL'),
    icon: 'i-lucide-circle-dot',
  },
  { key: 'call', label: t('TASKS.TYPES.CALL'), icon: 'i-lucide-phone' },
  { key: 'email', label: t('TASKS.TYPES.EMAIL'), icon: 'i-lucide-mail' },
  {
    key: 'meeting',
    label: t('TASKS.TYPES.MEETING'),
    icon: 'i-lucide-calendar',
  },
  {
    key: 'follow_up',
    label: t('TASKS.TYPES.FOLLOW_UP'),
    icon: 'i-lucide-refresh-cw',
  },
  {
    key: 'whatsapp',
    label: t('TASKS.TYPES.WHATSAPP'),
    icon: 'i-lucide-message-circle',
  },
  {
    key: 'contract',
    label: t('TASKS.TYPES.CONTRACT'),
    icon: 'i-lucide-file-signature',
  },
  {
    key: 'payment',
    label: t('TASKS.TYPES.PAYMENT'),
    icon: 'i-lucide-banknote',
  },
];

const filteredAssignees = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  if (!q) return agents.value ?? [];
  return (agents.value ?? [])
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

const filteredPriorityOptions = computed(() => {
  const q = prioritySearch.value.toLowerCase();
  if (!q) return PRIORITY_OPTIONS;
  return PRIORITY_OPTIONS.filter(o => o.label.toLowerCase().includes(q)).sort(
    (a, b) =>
      a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
  );
});

const filteredTypeOptions = computed(() => {
  const q = typeSearch.value.toLowerCase();
  if (!q) return TASK_TYPES;
  return TASK_TYPES.filter(o => o.label.toLowerCase().includes(q)).sort(
    (a, b) =>
      a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
  );
});

watch(filteredAssignees, () => {
  assigneeHighlight.value = 0;
});
watch(assigneeSearch, () => {
  assigneeHighlight.value = 0;
});
watch(filteredPriorityOptions, () => {
  priorityHighlight.value = 0;
});
watch(prioritySearch, () => {
  priorityHighlight.value = 0;
});
watch(filteredTypeOptions, () => {
  typeHighlight.value = 0;
});
watch(typeSearch, () => {
  typeHighlight.value = 0;
});

// ── Navegação por seta no menu principal ─────────────────────────
// A ordem espelha a dos botões no template. "delete" só existe para admin,
// então a lista é reativa: sem isso o índice do item some da tela e a seta
// atravessa uma posição morta.
const MAIN_ITEMS = computed(() => [
  'completed',
  'pending',
  'date',
  'assignee',
  'contact',
  'priority',
  'type',
  'tags_add',
  'tags_remove',
  ...(isAdmin.value ? ['delete'] : []),
  'save',
]);
const mainHighlight = ref(0);

// o template consulta pelo nome do item em vez de índice fixo — a numeração
// manual anterior estava dessincronizada da lista (type e tags trocados)
function isHighlighted(item) {
  return MAIN_ITEMS.value[mainHighlight.value] === item && !activeSubmenu.value;
}

function openSubmenu(menu) {
  const closing = activeSubmenu.value === menu;
  activeSubmenu.value = closing ? null : menu;
  labelSearch.value = '';
  if (!closing) {
    if (menu === 'tags_add') {
      calcSubmenuPos(tagsBtnRef, tagsAddSubmenuPos, 176);
      nextTick(() => labelSearchRef.value?.focus());
    } else if (menu === 'tags_remove') {
      calcSubmenuPos(tagsRemoveBtnRef, tagsRemoveSubmenuPos, 176);
      nextTick(() => labelRemoveSearchRef.value?.focus());
    } else if (menu === 'date') {
      calcSubmenuPos(dateBtnRef, dateSubmenuPos, 288);
    } else if (menu === 'assignee') {
      calcSubmenuPos(assigneeBtnRef, assigneeSubmenuPos, 208);
    } else if (menu === 'priority') {
      calcSubmenuPos(priorityBtnRef, prioritySubmenuPos, 160);
    } else if (menu === 'type') {
      calcSubmenuPos(typeBtnRef, typeSubmenuPos, 176);
    } else if (menu === 'contact') {
      calcSubmenuPos(contactBtnRef, contactSubmenuPos, 224);
    }
  }
}

const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  return (accountLabels.value ?? []).filter(l =>
    l.title.toLowerCase().includes(q)
  );
});

// ── Acumular alterações ───────────────────────────────────────────
function setStatus(status) {
  activeSubmenu.value = null;
  if (status === 'completed') {
    pendingChanges.value = {
      ...pendingChanges.value,
      status: 'completed',
      completed_at: new Date().toISOString(),
      delete: undefined,
    };
  } else {
    pendingChanges.value = {
      ...pendingChanges.value,
      status: 'pending',
      completed_at: null,
      delete: undefined,
    };
  }
}

function setDate({
  _close: shouldClose,
  due_date: dueDate,
  due_time: dueTime,
}) {
  if (shouldClose) {
    activeSubmenu.value = null;
  }
  const isoDate = buildDueIso(dueDate, dueTime);
  pendingChanges.value = { ...pendingChanges.value, due_date: isoDate };
}

function setAssignee(agent) {
  activeSubmenu.value = null;
  pendingChanges.value = {
    ...pendingChanges.value,
    assignee_id: agent?.id ?? null,
  };
}

function setContact(contact) {
  activeSubmenu.value = null;
  contactSearch.value = contact ? contact.name : '';
  contactResults.value = [];
  pendingChanges.value = {
    ...pendingChanges.value,
    contact_id: contact?.id ?? null,
    _contactName: contact?.name ?? null,
  };
}

function setPriority(key) {
  activeSubmenu.value = null;
  pendingChanges.value = { ...pendingChanges.value, priority: key };
}

function setType(key) {
  activeSubmenu.value = null;
  pendingChanges.value = { ...pendingChanges.value, task_type: key };
}

function toggleLabelAdd(title) {
  const current = pendingChanges.value.label_add ?? [];
  const updated = current.includes(title)
    ? current.filter(l => l !== title)
    : [...current, title];
  pendingChanges.value = { ...pendingChanges.value, label_add: updated };
}

function toggleLabelRemove(title) {
  const current = pendingChanges.value.label_remove ?? [];
  const updated = current.includes(title)
    ? current.filter(l => l !== title)
    : [...current, title];
  pendingChanges.value = {
    ...pendingChanges.value,
    label_remove: updated,
    label_remove_all: false,
  };
}

function setLabelRemoveAll() {
  pendingChanges.value = {
    ...pendingChanges.value,
    label_remove: [],
    label_remove_all: true,
  };
}

function setDelete() {
  activeSubmenu.value = null;
  pendingChanges.value = { delete: true };
}

// ── Execução ──────────────────────────────────────────────────────
const isExecuting = ref(false);

async function execute() {
  if (!hasPendingChanges.value) return;
  isExecuting.value = true;
  try {
    const p = pendingChanges.value;
    const ids = props.selectedIds;

    if (p.delete) {
      await store.dispatch('tasks/bulkDeleteTasks', ids);
    } else {
      const updatePayload = {};
      if (p.status) updatePayload.status = p.status;
      if (p.completed_at !== undefined)
        updatePayload.completed_at = p.completed_at;
      if (p.due_date !== undefined) updatePayload.due_date = p.due_date;
      if (p.assignee_id !== undefined)
        updatePayload.assignee_id = p.assignee_id;
      if (p.contact_id !== undefined) updatePayload.contact_id = p.contact_id;
      if (p.priority) updatePayload.priority = p.priority;
      if (p.task_type) updatePayload.task_type = p.task_type;

      if (Object.keys(updatePayload).length > 0) {
        await store.dispatch('tasks/bulkUpdateTasks', {
          ids,
          data: updatePayload,
        });
      }
      await Promise.all(
        (p.label_add ?? []).map(label =>
          store.dispatch('tasks/bulkLabelAddTasks', { ids, label })
        )
      );
      if (p.label_remove_all) {
        await store.dispatch('tasks/bulkLabelRemoveAllTasks', ids);
      } else {
        await Promise.all(
          (p.label_remove ?? []).map(label =>
            store.dispatch('tasks/bulkLabelRemoveTasks', { ids, label })
          )
        );
      }
    }
    emit('done');
  } finally {
    isExecuting.value = false;
    pendingChanges.value = {};
  }
}

// ── Navegação por teclado ─────────────────────────────────────────
function onSubmenuKeydown(e) {
  const menu = activeSubmenu.value;
  if (!menu || !['assignee', 'priority', 'type'].includes(menu)) return;

  const map = {
    assignee: {
      list: filteredAssignees,
      highlight: assigneeHighlight,
      search: assigneeSearch,
    },
    priority: {
      list: filteredPriorityOptions,
      highlight: priorityHighlight,
      search: prioritySearch,
    },
    type: {
      list: filteredTypeOptions,
      highlight: typeHighlight,
      search: typeSearch,
    },
  };
  const ctx = map[menu];

  if (e.key === 'Escape') {
    activeSubmenu.value = null;
    ctx.search.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    ctx.highlight.value = Math.min(
      ctx.highlight.value + 1,
      ctx.list.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    ctx.highlight.value = Math.max(ctx.highlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = ctx.list.value[ctx.highlight.value];
    if (!item) return;
    if (menu === 'assignee') setAssignee(item);
    else if (menu === 'priority') setPriority(item.key);
    else if (menu === 'type') setType(item.key);
  } else if (e.key === 'Backspace') {
    ctx.search.value = ctx.search.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    ctx.search.value += e.key;
  }
}

function onMainKeydown(e) {
  if (activeSubmenu.value) return;
  if (e.key === 'Escape') {
    emit('close');
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    mainHighlight.value = Math.min(
      mainHighlight.value + 1,
      MAIN_ITEMS.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    mainHighlight.value = Math.max(mainHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = MAIN_ITEMS.value[mainHighlight.value];
    if (item === 'completed') setStatus('completed');
    else if (item === 'pending') setStatus('pending');
    else if (item === 'date') openSubmenu('date');
    else if (item === 'assignee') openSubmenu('assignee');
    else if (item === 'contact') openSubmenu('contact');
    else if (item === 'priority') openSubmenu('priority');
    else if (item === 'type') openSubmenu('type');
    else if (item === 'tags_add') openSubmenu('tags_add');
    else if (item === 'tags_remove') openSubmenu('tags_remove');
    else if (item === 'delete' && isAdmin.value) setDelete();
    else if (item === 'save' && hasPendingChanges.value) execute();
  }
}

watch(activeSubmenu, (val, old) => {
  // Limpar search do submenu que fechou
  if (old === 'assignee') assigneeSearch.value = '';
  if (old === 'priority') prioritySearch.value = '';
  if (old === 'type') typeSearch.value = '';
  if (old === 'contact') {
    contactSearch.value = '';
    contactResults.value = [];
  }

  if (val === null) {
    // Voltou ao menu principal
    window.removeEventListener('keydown', onSubmenuKeydown);
    window.addEventListener('keydown', onMainKeydown);
  } else if (['assignee', 'priority', 'type'].includes(val)) {
    // Abriu submenu com lista navegável
    assigneeHighlight.value = 0;
    priorityHighlight.value = 0;
    typeHighlight.value = 0;
    window.removeEventListener('keydown', onMainKeydown);
    window.addEventListener('keydown', onSubmenuKeydown);
  } else {
    // Abriu submenu sem keydown (date, tags_add, tags_remove)
    window.removeEventListener('keydown', onMainKeydown);
    window.removeEventListener('keydown', onSubmenuKeydown);
  }
});

onMounted(() => window.addEventListener('keydown', onMainKeydown));
onUnmounted(() => {
  window.removeEventListener('keydown', onMainKeydown);
  window.removeEventListener('keydown', onSubmenuKeydown);
});
</script>

<template>
  <div
    class="bg-n-background border border-n-weak rounded-xl shadow-xl w-64 py-1 overflow-hidden"
  >
    <!-- Cabeçalho -->
    <div
      class="flex items-center justify-between px-3 py-2 border-b border-n-weak"
    >
      <span class="text-xs font-semibold text-n-slate-11">
        {{ t('TASKS.BULK.SELECTED', { count }) }}
      </span>
      <button
        class="text-n-slate-9 hover:text-n-slate-12 transition-colors"
        @click="emit('cancel')"
      >
        <span class="i-lucide-x size-3.5" />
      </button>
    </div>

    <!-- ── Menu de ações ── -->
    <!-- Marcar como concluído -->
    <button
      class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors whitespace-nowrap"
      :class="[
        pendingChanges.status === 'completed'
          ? 'text-n-teal-11 font-medium'
          : 'text-n-slate-12',
        isHighlighted('completed') ? 'bg-n-surface-2' : '',
      ]"
      @click="setStatus('completed')"
    >
      <span
        class="i-lucide-check-circle size-3.5 flex-shrink-0"
        :class="
          pendingChanges.status === 'completed'
            ? 'text-n-teal-9'
            : 'text-n-slate-9'
        "
      />
      {{ t('TASKS.BULK.ACTIONS.COMPLETE') }}
      <span
        v-if="pendingChanges.status === 'completed'"
        class="i-lucide-check size-3 ml-auto text-n-teal-9 flex-shrink-0"
      />
    </button>

    <!-- Marcar como não concluído -->
    <button
      class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors whitespace-nowrap"
      :class="[
        pendingChanges.status === 'pending'
          ? 'text-n-brand font-medium'
          : 'text-n-slate-12',
        isHighlighted('pending') ? 'bg-n-surface-2' : '',
      ]"
      @click="setStatus('pending')"
    >
      <span class="i-lucide-circle size-3.5 flex-shrink-0 text-n-slate-9" />
      {{ t('TASKS.BULK.ACTIONS.PENDING') }}
      <span
        v-if="pendingChanges.status === 'pending'"
        class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
      />
    </button>

    <div class="h-px bg-n-weak mx-2 my-1" />

    <!-- Data e horário -->
    <div>
      <button
        ref="dateBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="[
          activeSubmenu === 'date' || pendingChanges.due_date !== undefined
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12',
          isHighlighted('date') ? 'bg-n-surface-2' : '',
        ]"
        @click="openSubmenu('date')"
      >
        <span class="i-lucide-calendar size-3.5 flex-shrink-0" />
        {{ t('TASKS.BULK.ACTIONS.DATE') }}
        <span class="i-lucide-chevron-right size-3.5 ml-auto flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'date'"
          data-bulk-dropdown
          class="fixed z-[10020]"
          :style="{
            top: dateSubmenuPos.top + 'px',
            left: dateSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <TaskDatePicker
            :model-value="dateValue"
            @update:model-value="setDate"
            @clear-time="() => {}"
          />
        </div>
      </Teleport>
    </div>

    <!-- Responsável -->
    <div>
      <button
        ref="assigneeBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="[
          activeSubmenu === 'assignee' ||
          pendingChanges.assignee_id !== undefined
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12',
          isHighlighted('assignee') ? 'bg-n-surface-2' : '',
        ]"
        @click="openSubmenu('assignee')"
      >
        <span class="i-lucide-user size-3.5 flex-shrink-0" />
        <span
          v-if="activeSubmenu === 'assignee' && assigneeSearch"
          class="text-n-slate-10 flex-1 text-left"
        >
          {{ assigneeSearch }}
        </span>
        <span v-else class="flex-1 text-left">{{
          t('TASKS.BULK.ACTIONS.ASSIGNEE')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'assignee'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-52 overflow-hidden py-1"
          :style="{
            top: assigneeSubmenuPos.top + 'px',
            left: assigneeSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <template v-if="!assigneeSearch">
            <button
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
              :class="
                assigneeHighlight === -1
                  ? 'text-n-brand font-medium bg-n-alpha-1'
                  : 'text-n-slate-11'
              "
              @click="setAssignee(null)"
            >
              <span
                class="i-lucide-user-x size-3.5 text-n-slate-9 flex-shrink-0"
              />
              {{ t('TASKS.BULK.UNASSIGN') }}
            </button>
            <div class="h-px bg-n-weak my-1" />
          </template>
          <button
            v-for="(agent, idx) in filteredAssignees"
            :key="agent.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              idx === assigneeHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : pendingChanges.assignee_id === agent.id
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="setAssignee(agent)"
          >
            <div
              class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white overflow-hidden"
              :style="
                agent.thumbnail
                  ? {
                      backgroundImage: `url(${agent.thumbnail})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : { backgroundColor: avatarColor(agent) }
              "
            >
              <span v-if="!agent.thumbnail">{{ initials(agent.name) }}</span>
            </div>
            <span class="truncate">{{ agent.name }}</span>
            <span
              v-if="pendingChanges.assignee_id === agent.id"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <p
            v-if="assigneeSearch && !filteredAssignees.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
          </p>
          <div v-if="assigneeSearch" class="border-t border-n-weak my-1" />
          <button
            v-if="assigneeSearch"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-11"
            @click="setAssignee(null)"
          >
            <span
              class="i-lucide-user-x size-3.5 text-n-slate-9 flex-shrink-0"
            />
            {{ t('TASKS.BULK.UNASSIGN') }}
          </button>
        </div>
      </Teleport>
    </div>

    <!-- Atribuir ao contato -->
    <div>
      <button
        ref="contactBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="[
          activeSubmenu === 'contact' || pendingChanges.contact_id !== undefined
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12',
          isHighlighted('contact') ? 'bg-n-surface-2' : '',
        ]"
        @click="openSubmenu('contact')"
      >
        <span class="i-lucide-building-2 size-3.5 flex-shrink-0" />
        <span
          v-if="pendingChanges._contactName"
          class="flex-1 text-left truncate"
        >
          {{ pendingChanges._contactName }}
        </span>
        <span
          v-else-if="activeSubmenu === 'contact' && contactSearch"
          class="text-n-slate-10 flex-1 text-left truncate"
        >
          {{ contactSearch }}
        </span>
        <span v-else class="flex-1 text-left">{{
          t('TASKS.BULK.ASSIGN_CONTACT')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'contact'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-56 overflow-hidden"
          :style="{
            top: contactSubmenuPos.top + 'px',
            left: contactSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <div class="p-1.5 border-b border-n-weak">
            <input
              v-model="contactSearch"
              class="w-full text-xs px-2 py-1 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none"
              :placeholder="t('TASKS.BULK.SEARCH_CONTACT_PLACEHOLDER')"
              @input="searchContacts(contactSearch)"
            />
          </div>
          <div class="max-h-52 overflow-y-auto py-1">
            <button
              v-if="!contactSearch"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-11"
              @click="setContact(null)"
            >
              <span
                class="i-lucide-user-x size-3.5 text-n-slate-9 flex-shrink-0"
              />
              {{ t('TASKS.BULK.UNASSIGN_CONTACT') }}
            </button>
            <div v-if="!contactSearch" class="h-px bg-n-weak my-1" />
            <template v-if="contactSearching">
              <p class="px-3 py-2 text-sm text-n-slate-10 italic">
                {{ t('TASKS.SMART_INPUT.SEARCHING') }}
              </p>
            </template>
            <template v-else-if="contactSearch && !contactResults.length">
              <p class="px-3 py-2 text-sm text-n-slate-10 italic">
                {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
              </p>
            </template>
            <button
              v-for="(contact, idx) in contactResults"
              :key="contact.id"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
              :class="
                idx === contactHighlight
                  ? 'text-n-brand font-medium bg-n-alpha-1'
                  : pendingChanges.contact_id === contact.id
                    ? 'text-n-brand font-medium'
                    : 'text-n-slate-12'
              "
              @click="setContact(contact)"
            >
              <div
                class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white overflow-hidden"
                :style="
                  contact.thumbnail
                    ? {
                        backgroundImage: `url(${contact.thumbnail})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }
                    : { backgroundColor: avatarColor(contact.name) }
                "
              >
                <span v-if="!contact.thumbnail">{{
                  initials(contact.name)
                }}</span>
              </div>
              <div class="flex flex-col items-start min-w-0">
                <span class="truncate text-xs font-medium leading-tight">{{
                  contact.name
                }}</span>
                <span
                  v-if="contact.company_name"
                  class="truncate text-[10px] text-n-slate-10 leading-tight"
                >
                  {{ contact.company_name }}
                </span>
              </div>
              <span
                v-if="pendingChanges.contact_id === contact.id"
                class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
              />
            </button>
          </div>
        </div>
      </Teleport>
    </div>

    <!-- Prioridade -->
    <div>
      <button
        ref="priorityBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="[
          activeSubmenu === 'priority' || pendingChanges.priority
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12',
          isHighlighted('priority') ? 'bg-n-surface-2' : '',
        ]"
        @click="openSubmenu('priority')"
      >
        <span class="i-lucide-flag size-3.5 flex-shrink-0" />
        <span
          v-if="activeSubmenu === 'priority' && prioritySearch"
          class="text-n-slate-10 flex-1 text-left"
        >
          {{ prioritySearch }}
        </span>
        <span v-else class="flex-1 text-left">{{
          t('TASKS.BULK.ACTIONS.PRIORITY')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'priority'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-40 py-1 overflow-hidden"
          :style="{
            top: prioritySubmenuPos.top + 'px',
            left: prioritySubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <button
            v-for="(opt, idx) in filteredPriorityOptions"
            :key="opt.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              idx === priorityHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : pendingChanges.priority === opt.key
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="setPriority(opt.key)"
          >
            <span
              class="i-lucide-flag size-3.5 flex-shrink-0"
              :class="opt.dot"
            />
            {{ opt.label }}
            <span
              v-if="pendingChanges.priority === opt.key"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <p
            v-if="prioritySearch && !filteredPriorityOptions.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
          </p>
        </div>
      </Teleport>
    </div>

    <!-- Tipo -->
    <div>
      <button
        ref="typeBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="[
          activeSubmenu === 'type' || pendingChanges.task_type
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12',
          isHighlighted('type') ? 'bg-n-surface-2' : '',
        ]"
        @click="openSubmenu('type')"
      >
        <span class="i-lucide-layers size-3.5 flex-shrink-0" />
        <span
          v-if="activeSubmenu === 'type' && typeSearch"
          class="text-n-slate-10 flex-1 text-left"
        >
          {{ typeSearch }}
        </span>
        <span v-else class="flex-1 text-left">{{
          t('TASKS.BULK.ACTIONS.TYPE')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'type'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-44 py-1 overflow-hidden"
          :style="{
            top: typeSubmenuPos.top + 'px',
            left: typeSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <button
            v-for="(type, idx) in filteredTypeOptions"
            :key="type.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              idx === typeHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : pendingChanges.task_type === type.key
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="setType(type.key)"
          >
            <span class="size-3.5 flex-shrink-0" :class="[type.icon]" />
            {{ type.label }}
            <span
              v-if="pendingChanges.task_type === type.key"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <p
            v-if="typeSearch && !filteredTypeOptions.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
          </p>
        </div>
      </Teleport>
    </div>

    <!-- Inserir tags -->
    <div>
      <button
        ref="tagsBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="[
          activeSubmenu === 'tags_add' || pendingChanges.label_add?.length
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12',
          isHighlighted('tags_add') ? 'bg-n-surface-2' : '',
        ]"
        @click="openSubmenu('tags_add')"
      >
        <span class="i-lucide-tag size-3.5 flex-shrink-0" />
        {{ t('TASKS.BULK.ACTIONS.TAGS_ADD') }}
        <span
          v-if="pendingChanges.label_add?.length"
          class="ml-1 text-xs bg-n-brand text-white rounded-full px-1.5 py-px"
        >
          {{ pendingChanges.label_add.length }}
        </span>
        <span class="i-lucide-chevron-right size-3.5 ml-auto flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'tags_add'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden"
          :style="{
            top: tagsAddSubmenuPos.top + 'px',
            left: tagsAddSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <div class="p-1.5 border-b border-n-weak">
            <input
              ref="labelSearchRef"
              v-model="labelSearch"
              class="w-full text-xs px-2 py-1 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none"
              :placeholder="t('TASKS.BULK.SEARCH_LABEL_PLACEHOLDER')"
            />
          </div>
          <div class="max-h-48 overflow-y-auto py-1">
            <button
              v-for="lbl in filteredLabels"
              :key="lbl.id"
              class="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-n-alpha-1"
              :class="
                pendingChanges.label_add?.includes(lbl.title)
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
              "
              @click="toggleLabelAdd(lbl.title)"
            >
              <span
                class="size-2 rounded-full flex-shrink-0"
                :style="{ backgroundColor: lbl.color }"
              />
              {{ lbl.title }}
              <span
                v-if="pendingChanges.label_add?.includes(lbl.title)"
                class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
              />
            </button>
          </div>
        </div>
      </Teleport>
    </div>

    <!-- Remover tags -->
    <div>
      <button
        ref="tagsRemoveBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="[
          activeSubmenu === 'tags_remove' || pendingChanges.label_remove?.length
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12',
          isHighlighted('tags_remove') ? 'bg-n-surface-2' : '',
        ]"
        @click="openSubmenu('tags_remove')"
      >
        <span class="relative flex-shrink-0 size-3.5">
          <span class="i-lucide-tag size-3.5" />
          <span class="absolute inset-0 flex items-center justify-center">
            <span
              class="block w-[18px] h-px bg-current rotate-[-45deg] translate-x-[-1px]"
            />
          </span>
        </span>
        {{ t('TASKS.BULK.ACTIONS.TAGS_REMOVE') }}
        <span
          v-if="pendingChanges.label_remove?.length"
          class="ml-1 text-xs bg-n-ruby-9 text-white rounded-full px-1.5 py-px"
        >
          {{ pendingChanges.label_remove.length }}
        </span>
        <span class="i-lucide-chevron-right size-3.5 ml-auto flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'tags_remove'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden"
          :style="{
            top: tagsRemoveSubmenuPos.top + 'px',
            left: tagsRemoveSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <div class="p-1.5 border-b border-n-weak">
            <input
              ref="labelRemoveSearchRef"
              v-model="labelSearch"
              class="w-full text-xs px-2 py-1 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none"
              :placeholder="t('TASKS.BULK.SEARCH_LABEL_PLACEHOLDER')"
            />
          </div>
          <div class="max-h-48 overflow-y-auto py-1">
            <button
              v-for="lbl in filteredLabels"
              :key="lbl.id"
              class="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-n-alpha-1"
              :class="
                pendingChanges.label_remove?.includes(lbl.title)
                  ? 'text-n-ruby-11 font-medium'
                  : 'text-n-slate-12'
              "
              @click="toggleLabelRemove(lbl.title)"
            >
              <span
                class="size-2 rounded-full flex-shrink-0"
                :style="{ backgroundColor: lbl.color }"
              />
              {{ lbl.title }}
              <span
                v-if="pendingChanges.label_remove?.includes(lbl.title)"
                class="i-lucide-check size-3 ml-auto text-n-ruby-9 flex-shrink-0"
              />
            </button>
          </div>
          <div class="h-px bg-n-weak mx-1" />
          <button
            class="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-n-ruby-2 transition-colors"
            :class="
              pendingChanges.label_remove_all
                ? 'text-n-ruby-11 font-medium bg-n-ruby-2'
                : 'text-n-ruby-11'
            "
            @click="setLabelRemoveAll"
          >
            <span class="i-lucide-tags size-3.5 flex-shrink-0" />
            {{ t('TASKS.BULK.REMOVE_ALL_TAGS') }}
            <span
              v-if="pendingChanges.label_remove_all"
              class="i-lucide-check size-3 ml-auto text-n-ruby-9 flex-shrink-0"
            />
          </button>
        </div>
      </Teleport>
    </div>

    <div v-if="isAdmin" class="h-px bg-n-weak mx-2 my-1" />

    <!-- Excluir tarefas -->
    <button
      v-if="isAdmin"
      class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-ruby-2 transition-colors"
      :class="[
        pendingChanges.delete
          ? 'text-n-ruby-11 font-medium bg-n-ruby-2'
          : 'text-n-ruby-11',
        isHighlighted('delete') ? 'bg-n-ruby-3' : '',
      ]"
      @click="setDelete"
    >
      <span class="i-lucide-trash-2 size-3.5 flex-shrink-0" />
      {{ t('TASKS.BULK.DELETE_TASKS') }}
      <span
        v-if="pendingChanges.delete"
        class="i-lucide-check size-3 ml-auto text-n-ruby-9 flex-shrink-0"
      />
    </button>

    <!-- Rodapé: botão Salvar -->
    <div class="flex justify-end px-3 py-2 border-t border-n-weak mt-1">
      <button
        class="px-3 py-1.5 text-xs font-medium rounded-lg transition-all"
        :class="[
          hasPendingChanges
            ? 'bg-n-brand text-white hover:brightness-110 cursor-pointer'
            : 'bg-n-surface-2 text-n-slate-9 cursor-not-allowed',
          isHighlighted('save') && hasPendingChanges ? 'brightness-110' : '',
        ]"
        :disabled="!hasPendingChanges || isExecuting"
        @click="hasPendingChanges && execute()"
      >
        <span
          v-if="isExecuting"
          class="i-lucide-loader size-3 animate-spin inline-block mr-1"
        />
        {{ t('TASKS.BULK.APPLY') }}
      </button>
    </div>
  </div>
</template>
